import type { Router } from "vue-router";

/**
 * {@link import("./staleChunkRecovery").recoverFromStaleChunks} catches an import that has
 * already failed, by which point the user has clicked into a dead route. A tab left open
 * across a deploy can know it is out of date before asking for anything: index.html names the
 * entry chunk, and every build renames it. So check when the user comes back to the tab, and
 * swap their next navigation for a full page load — same route, nothing lost.
 */

/** Tab switching is cheap and frequent; hitting the server on each one is not. */
const RECHECK_INTERVAL_MS = 60_000;

/** index.html carries exactly one — Vite's entry. Dynamic imports add no <script>. */
function entryScriptOf(root: Document): string | null {
  return root.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute("src") ?? null;
}

async function deployedEntryScript(): Promise<string | null> {
  const response = await fetch(import.meta.env.BASE_URL, { cache: "no-store" });
  if (!response.ok) return null;

  const html = new DOMParser().parseFromString(await response.text(), "text/html");
  return entryScriptOf(html);
}

export function watchForStaleBuild(router: Router): void {
  const bootedFrom = entryScriptOf(document);
  let lastCheckedAt = 0;
  let isStale = false;

  async function check(): Promise<void> {
    if (isStale || !bootedFrom || Date.now() - lastCheckedAt < RECHECK_INTERVAL_MS) return;
    lastCheckedAt = Date.now();

    try {
      const deployed = await deployedEntryScript();
      isStale = !!deployed && deployed !== bootedFrom;
    } catch {
      // Offline, or the server hiccuped. Assume nothing; we get another chance.
    }
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check();
  });
  window.addEventListener("focus", () => void check());

  router.beforeEach((to) => {
    if (!isStale) return true;

    window.location.assign(to.fullPath);
    return false;
  });
}
